const DEFAULT_BACKEND_BASE_URL = "https://api.city-scope.co.uk";

const FALLBACK_COUNTS = {
  planningApplications: 8980,
  shortTermLetApplications: 1396,
};

function normalizeBackendBaseUrl(value) {
  return String(value || DEFAULT_BACKEND_BASE_URL)
    .trim()
    .replace(/\/applications\/?$/i, "")
    .replace(/\/short-term-lets\/?$/i, "")
    .replace(/\/+$/, "");
}

export function getCityScopeBackendBaseUrl() {
  return normalizeBackendBaseUrl(
    process.env.CITY_SCOPE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      process.env.NEXT_PUBLIC_APPLICATIONS_API_URL ||
      process.env.APPLICATIONS_API_URL
  );
}

function toCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
}

function deriveCouncilPlanningApplicationCounts(payload = {}) {
  const explicitCounts =
    payload.counts && typeof payload.counts === "object" ? payload.counts : {};
  const councilBreakdown =
    payload.by_council && typeof payload.by_council === "object"
      ? payload.by_council
      : {};
  const councilSlugs = new Set([
    ...Object.keys(explicitCounts),
    ...Object.keys(councilBreakdown),
  ]);

  return Array.from(councilSlugs).reduce((counts, slug) => {
    const explicit = explicitCounts[slug];
    const breakdown = councilBreakdown[slug];
    const value =
      explicit ??
      (breakdown && typeof breakdown === "object"
        ? breakdown.planning_applications
        : undefined);
    counts[slug] = toCount(value);
    return counts;
  }, {});
}

export function deriveBackendApplicationCounts(payload = {}) {
  const planningApplications = toCount(payload.planning_applications);
  const shortTermLetApplications = toCount(payload.short_term_let_applications);

  return {
    planningApplications,
    shortTermLetApplications,
    totalApplications: planningApplications + shortTermLetApplications,
    councilPlanningApplications: deriveCouncilPlanningApplicationCounts(payload),
  };
}

export function getFallbackBackendApplicationCounts() {
  return {
    ...FALLBACK_COUNTS,
    totalApplications:
      FALLBACK_COUNTS.planningApplications + FALLBACK_COUNTS.shortTermLetApplications,
    councilPlanningApplications: {
      edinburgh: 6053,
      midlothian: 873,
      "east-lothian": 1223,
      "west-lothian": 831,
    },
  };
}

export async function fetchBackendApplicationCounts({
  timeoutMs = 3500,
  fetchImpl = fetch,
} = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const url = `${getCityScopeBackendBaseUrl()}/stats/application-counts`;
    const response = await fetchImpl(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`Backend stats returned ${response.status}`);
    }

    return deriveBackendApplicationCounts(await response.json());
  } finally {
    clearTimeout(timeoutId);
  }
}
